/**
 * Landing Page - First-time visitor experience
 * 
 * Target: Parents (the buyers)
 * Goal: Explain value prop, build trust, convert to signup
 * 
 * Key messages:
 * 1. "Your kid earns Robux by leveling up their math"
 * 2. Parent-controlled, COPPA compliant
 * 3. Real math education (not just gamification fluff)
 */

import { useState, useEffect } from 'react';

interface LandingPageProps {
  onEnterApp: () => void;
  onParentSignup: () => void;
  isReturningUser?: boolean;
}

export function LandingPage({ onEnterApp, onParentSignup, isReturningUser }: LandingPageProps) {
  const [showDemo, setShowDemo] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  
  // Rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen bg-blox-darker overflow-hidden">
      {/* Animated background */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blox-accent/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blox-rare/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 w-64 h-64 bg-blox-xp/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
      </div>

      {/* Navigation */}
      <nav className="relative z-10 max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blox-accent to-blox-rare flex items-center justify-center">
            <span className="text-xl">🎯</span>
          </div>
          <span className="font-display text-2xl text-white tracking-wider">
            GOAL<span className="text-blox-accent">BLOX</span>
          </span>
        </div>
        
        <div className="flex items-center gap-4">
          <a href="#how-it-works" className="text-slate-300 hover:text-white transition-colors text-sm">
            How It Works
          </a>
          <a href="#for-parents" className="text-slate-300 hover:text-white transition-colors text-sm">
            For Parents
          </a>
          {isReturningUser ? (
            <button
              onClick={onEnterApp}
              className="px-5 py-2.5 rounded-xl bg-blox-accent text-blox-dark font-semibold text-sm hover:bg-blox-accent/90 transition-all"
            >
              Go to Dashboard →
            </button>
          ) : (
            <button
              onClick={onParentSignup}
              className="px-5 py-2.5 rounded-xl bg-blox-accent text-blox-dark font-semibold text-sm hover:bg-blox-accent/90 transition-all"
            >
              Get Started Free
            </button>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blox-xp/10 border border-blox-xp/30 mb-6">
              <span className="w-2 h-2 rounded-full bg-blox-xp animate-pulse" />
              <span className="text-blox-xp text-sm font-medium">
                Parent-controlled • COPPA compliant
              </span>
            </div>
            
            <h1 className="font-display text-5xl lg:text-6xl text-white leading-tight mb-6">
              Your kid earns{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blox-accent to-blox-rare">
                Robux
              </span>
              <br />
              by leveling up{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blox-xp to-blox-gold">
                their math
              </span>
            </h1>
            
            <p className="text-xl text-slate-300 mb-8 leading-relaxed">
              Real math challenges. Real rewards. You control the payout.
              <br />
              <span className="text-slate-400">
                From arithmetic to calculus — one story at a time.
              </span>
            </p>
            
            <div className="flex flex-wrap gap-4 mb-8">
              <button
                onClick={onParentSignup}
                className="px-8 py-4 rounded-2xl bg-gradient-to-r from-blox-accent to-blox-accent/80 text-blox-dark font-bold text-lg hover:shadow-lg hover:shadow-blox-accent/30 transition-all hover:scale-105"
              >
                Start Free as Parent
              </button>
              <button
                onClick={onEnterApp}
                className="px-8 py-4 rounded-2xl bg-blox-surface border border-blox-card text-white font-semibold text-lg hover:bg-blox-card transition-all"
              >
                Try Demo First
              </button>
              <button
                onClick={() => setShowDemo(true)}
                className="px-6 py-4 rounded-2xl bg-blox-card/50 text-slate-400 text-lg hover:bg-blox-card transition-all flex items-center gap-2"
              >
                <span>▶</span> Watch Video
              </button>
            </div>
            
            {/* Trust badges */}
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center gap-2">
                <span className="text-blox-xp">✓</span> No credit card required
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blox-xp">✓</span> Free tier available
              </div>
              <div className="flex items-center gap-2">
                <span className="text-blox-xp">✓</span> Setup in 2 minutes
              </div>
            </div>
          </div>
          
          {/* Right: App Preview */}
          <div className="relative">
            {/* Phone mockup */}
            <div className="relative mx-auto w-80 h-[580px] rounded-[3rem] bg-gradient-to-b from-slate-700 to-slate-800 p-3 shadow-2xl shadow-black/50">
              <div className="w-full h-full rounded-[2.5rem] bg-blox-darker overflow-hidden">
                {/* App screen content */}
                <div className="p-4">
                  {/* Mini header */}
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-display text-lg text-white">QUEST<span className="text-blox-accent">HUB</span></span>
                    <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-blox-surface">
                      <span>🪙</span>
                      <span className="text-blox-gold font-display">125</span>
                    </div>
                  </div>
                  
                  {/* XP Bar */}
                  <div className="mb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-blox-accent flex items-center justify-center font-display text-blox-dark">5</div>
                      <div className="flex-1 h-3 rounded-full bg-blox-surface overflow-hidden">
                        <div className="h-full w-2/3 bg-gradient-to-r from-blox-xp to-blox-accent rounded-full animate-pulse" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Quest card */}
                  <div className="p-4 rounded-2xl bg-blox-card border border-blox-surface mb-3">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">🌱</span>
                      <div>
                        <p className="font-display text-white">Garden Coins</p>
                        <p className="text-xs text-slate-400">Rate of Change</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 rounded text-xs bg-blox-xp/20 text-blox-xp">+30 XP</span>
                      <span className="text-blox-accent text-sm">Start →</span>
                    </div>
                  </div>
                  
                  {/* Another card */}
                  <div className="p-4 rounded-2xl bg-blox-surface/50 border border-blox-card">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">🔍</span>
                      <div>
                        <p className="font-display text-white">Proof Quest</p>
                        <p className="text-xs text-slate-400">Logical Deduction</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 rounded text-xs bg-blox-rare/20 text-blox-rare">+50 XP</span>
                      <span className="text-slate-400 text-sm">Locked 🔒</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Floating badges */}
            <div className="absolute -left-8 top-20 p-3 rounded-xl bg-blox-card border border-blox-surface shadow-xl animate-float">
              <div className="flex items-center gap-2">
                <span className="text-2xl">🎓</span>
                <div>
                  <p className="text-white font-semibold text-sm">Level Up!</p>
                  <p className="text-xs text-slate-400">Algebra I unlocked</p>
                </div>
              </div>
            </div>
            
            <div className="absolute -right-8 bottom-32 p-3 rounded-xl bg-blox-xp/10 border border-blox-xp/30 shadow-xl animate-float" style={{ animationDelay: '1s' }}>
              <div className="flex items-center gap-2">
                <span className="text-2xl">💰</span>
                <div>
                  <p className="text-blox-xp font-semibold text-sm">+25 Robux</p>
                  <p className="text-xs text-slate-400">Reward approved!</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="relative z-10 py-24 bg-blox-dark/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="font-display text-4xl text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 text-lg">
              Simple for parents. Fun for kids. Educational for real.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative p-6 rounded-2xl bg-blox-card border border-blox-surface">
              <div className="absolute -top-4 left-6 w-8 h-8 rounded-full bg-blox-accent flex items-center justify-center font-display text-blox-dark">
                1
              </div>
              <div className="pt-4">
                <span className="text-4xl mb-4 block">👨‍👩‍👧</span>
                <h3 className="font-display text-xl text-white mb-2">Parent Sets Challenge</h3>
                <p className="text-slate-400">
                  Pick a math topic, set the reward amount (like "25 Robux for completing algebra"). 
                  You're in control.
                </p>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="relative p-6 rounded-2xl bg-blox-card border border-blox-surface">
              <div className="absolute -top-4 left-6 w-8 h-8 rounded-full bg-blox-accent flex items-center justify-center font-display text-blox-dark">
                2
              </div>
              <div className="pt-4">
                <span className="text-4xl mb-4 block">🎮</span>
                <h3 className="font-display text-xl text-white mb-2">Kid Completes Quest</h3>
                <p className="text-slate-400">
                  Gaming-style interface. Voice hints. Progress tracking. 
                  Same story from arithmetic to calculus.
                </p>
              </div>
            </div>
            
            {/* Step 3 */}
            <div className="relative p-6 rounded-2xl bg-blox-card border border-blox-surface">
              <div className="absolute -top-4 left-6 w-8 h-8 rounded-full bg-blox-accent flex items-center justify-center font-display text-blox-dark">
                3
              </div>
              <div className="pt-4">
                <span className="text-4xl mb-4 block">💰</span>
                <h3 className="font-display text-xl text-white mb-2">You Approve Payout</h3>
                <p className="text-slate-400">
                  Review what they learned. Approve the Robux reward. 
                  No auto-transfers — you stay in control.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Parents Section */}
      <section id="for-parents" className="relative z-10 py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Features */}
            <div>
              <h2 className="font-display text-4xl text-white mb-6">
                Built for parents who care about{' '}
                <span className="text-blox-accent">real learning</span>
              </h2>
              
              <div className="space-y-6">
                {[
                  {
                    icon: '📚',
                    title: 'Not just gamification fluff',
                    desc: 'Actual math curriculum from pre-algebra through calculus. Proofs, derivatives, the real stuff.',
                  },
                  {
                    icon: '🎯',
                    title: 'One story, many levels',
                    desc: '"Garden Coins" teaches rate × time, then slope, then derivatives — same context, deeper math.',
                  },
                  {
                    icon: '🔒',
                    title: 'You control everything',
                    desc: 'Set rewards, approve payouts, see progress. No surprises. COPPA compliant.',
                  },
                  {
                    icon: '🧠',
                    title: 'Proofs, not just answers',
                    desc: '"Verify your work" ≠ "Prove your work". We teach actual logical deduction.',
                  },
                ].map((feature, i) => (
                  <div 
                    key={i}
                    className={`p-4 rounded-xl transition-all cursor-pointer ${
                      activeFeature === i 
                        ? 'bg-blox-accent/10 border border-blox-accent/30' 
                        : 'bg-blox-surface/50 border border-transparent hover:border-blox-card'
                    }`}
                    onClick={() => setActiveFeature(i)}
                  >
                    <div className="flex items-start gap-4">
                      <span className="text-2xl">{feature.icon}</span>
                      <div>
                        <h3 className="font-semibold text-white mb-1">{feature.title}</h3>
                        <p className="text-sm text-slate-400">{feature.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Right: Dashboard preview */}
            <div className="p-6 rounded-2xl bg-blox-card border border-blox-surface">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="ml-4 text-xs text-slate-500">Parent Dashboard</span>
              </div>
              
              {/* Mock dashboard content */}
              <div className="space-y-4">
                {/* Child card */}
                <div className="p-4 rounded-xl bg-blox-surface">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blox-accent/20 flex items-center justify-center">
                        👧
                      </div>
                      <div>
                        <p className="font-semibold text-white">Isabella</p>
                        <p className="text-xs text-slate-400">Level 5 • 2,340 XP</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-blox-xp font-display">3 🔥</p>
                      <p className="text-xs text-slate-400">day streak</p>
                    </div>
                  </div>
                  
                  {/* Progress summary */}
                  <div className="p-3 rounded-lg bg-blox-dark/50">
                    <p className="text-xs text-slate-500 mb-1">AI Summary</p>
                    <p className="text-sm text-slate-300">
                      "Strong grasp of power rule derivatives. Working on proof construction."
                    </p>
                  </div>
                </div>
                
                {/* Pending payout */}
                <div className="p-4 rounded-xl bg-blox-gold/10 border border-blox-gold/30">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-blox-gold font-semibold">Pending Approval</p>
                      <p className="text-xs text-slate-400">Garden Coins - Rate Quest</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button className="px-3 py-1.5 rounded-lg bg-blox-gold text-blox-dark text-sm font-semibold">
                        Approve 25 Robux
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Cost monitor */}
                <div className="p-3 rounded-lg bg-blox-surface/50">
                  <p className="text-xs text-slate-500 mb-2">Today's AI Usage</p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-2 rounded-full bg-blox-dark overflow-hidden">
                      <div className="h-full w-1/4 bg-blox-accent rounded-full" />
                    </div>
                    <span className="text-xs text-slate-400">$0.42 / $5.00</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Math Journey */}
      <section className="relative z-10 py-24 bg-blox-dark/50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl text-white mb-4">
              One Story. K-12 Math.
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Your child uses the same familiar "game" from elementary through calculus. 
              Only the question type changes — building deep understanding without constant context-switching.
            </p>
          </div>
          
          {/* Journey visualization */}
          <div className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blox-xp via-blox-accent to-blox-rare" />
            
            <div className="space-y-8">
              {[
                { level: 'Elementary', question: '"How many coins after 5 minutes?"', concept: 'Arithmetic', color: 'blox-xp' },
                { level: 'Pre-Algebra', question: '"Write an equation for coins vs time"', concept: 'Variables', color: 'blox-xp' },
                { level: 'Algebra I', question: '"What is the slope?"', concept: 'Rate of change', color: 'blox-accent' },
                { level: 'Pre-Calculus', question: '"Is the rate constant?"', concept: 'Nonlinearity', color: 'blox-accent' },
                { level: 'Calculus', question: '"How fast RIGHT NOW at t=4?"', concept: 'Derivatives', color: 'blox-rare' },
              ].map((item, i) => (
                <div 
                  key={i} 
                  className={`flex items-center gap-8 ${i % 2 === 0 ? 'flex-row' : 'flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${i % 2 === 0 ? 'text-right' : 'text-left'}`}>
                    <p className={`font-display text-${item.color}`}>{item.level}</p>
                    <p className="text-white font-mono text-sm mt-1">{item.question}</p>
                    <p className="text-slate-500 text-xs mt-1">{item.concept}</p>
                  </div>
                  <div className={`w-4 h-4 rounded-full bg-${item.color} ring-4 ring-blox-dark z-10`} />
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
          
          <p className="text-center text-slate-400 mt-12">
            <span className="text-white">Same story: 🌱 Garden Coins</span> — deeper math at every level
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative z-10 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-4xl text-white mb-6">
            Ready to turn screen time into{' '}
            <span className="text-blox-accent">learning time</span>?
          </h2>
          <p className="text-slate-400 text-lg mb-8">
            Free to start. No credit card required. Setup in 2 minutes.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4">
            <button
              onClick={onParentSignup}
              className="px-8 py-4 rounded-2xl bg-gradient-to-r from-blox-accent to-blox-accent/80 text-blox-dark font-bold text-lg hover:shadow-lg hover:shadow-blox-accent/30 transition-all hover:scale-105"
            >
              Get Started as Parent
            </button>
            <button
              onClick={onEnterApp}
              className="px-8 py-4 rounded-2xl bg-blox-surface border border-blox-card text-white font-semibold text-lg hover:bg-blox-card transition-all"
            >
              Try Demo First
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative z-10 py-12 border-t border-blox-surface">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-between items-center gap-6">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blox-accent to-blox-rare flex items-center justify-center">
                <span>🎯</span>
              </div>
              <span className="font-display text-lg text-white">GOAL<span className="text-blox-accent">BLOX</span></span>
            </div>
            
            <div className="flex items-center gap-6 text-sm text-slate-400">
              <a href="#" className="hover:text-white transition-colors">Privacy</a>
              <a href="#" className="hover:text-white transition-colors">Terms</a>
              <a href="#" className="hover:text-white transition-colors">Contact</a>
              <span>© 2025 GoalBlox</span>
            </div>
          </div>
          
          <p className="mt-6 text-xs text-slate-600 text-center">
            GoalBlox is not affiliated with, endorsed by, or sponsored by Roblox Corporation. 
            "Roblox" and "Robux" are trademarks of Roblox Corporation.
          </p>
        </div>
      </footer>

      {/* Demo Modal */}
      {showDemo && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-blox-card rounded-2xl max-w-3xl w-full overflow-hidden animate-pop-in">
            <div className="p-4 border-b border-blox-surface flex items-center justify-between">
              <h3 className="font-display text-lg text-white">Watch Demo</h3>
              <button
                onClick={() => setShowDemo(false)}
                className="p-2 rounded-lg hover:bg-blox-surface transition-colors text-slate-400"
              >
                ✕
              </button>
            </div>
            <div className="aspect-video bg-blox-dark flex items-center justify-center">
              <div className="text-center">
                <span className="text-6xl mb-4 block">🎬</span>
                <p className="text-slate-400">Demo video coming soon!</p>
                <button
                  onClick={() => { setShowDemo(false); onEnterApp(); }}
                  className="mt-4 px-6 py-2 rounded-xl bg-blox-accent text-blox-dark font-semibold"
                >
                  Try the Live App Instead →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// RETURNING USER BANNER
// ============================================================================

interface WelcomeBackBannerProps {
  userName: string;
  onContinue: () => void;
  onViewLanding: () => void;
}

export function WelcomeBackBanner({ userName, onContinue, onViewLanding }: WelcomeBackBannerProps) {
  return (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-slide-up">
      <div className="px-6 py-4 rounded-2xl bg-blox-card border border-blox-accent/30 shadow-2xl shadow-black/50 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-blox-accent/20 flex items-center justify-center">
          👋
        </div>
        <div>
          <p className="text-white font-semibold">Welcome back, {userName}!</p>
          <p className="text-sm text-slate-400">Ready to continue your quests?</p>
        </div>
        <div className="flex gap-2 ml-4">
          <button
            onClick={onContinue}
            className="px-4 py-2 rounded-xl bg-blox-accent text-blox-dark font-semibold text-sm"
          >
            Go to Dashboard
          </button>
          <button
            onClick={onViewLanding}
            className="px-4 py-2 rounded-xl bg-blox-surface text-slate-300 text-sm"
          >
            View Homepage
          </button>
        </div>
      </div>
    </div>
  );
}
